import { useState, useEffect } from 'react';
import StatBlock from '../Statblock/StatBlock.js';
import { effectImgMap, conditionImgMap, generateUniqueId, dummyDefault, envObject, COLOR_RED, COLOR_GREEN, INIT_ENCOUNTER_NAME } from '../../constants';
import DropdownMenu from './Dropdowns/DropdownMenu.js';
import EncounterList from './EncounterList'
import { useUser } from '../../../../../providers/UserProvider.js';
import EncounterColumnTop from './EncounterColumnTop.js';
import Effect from './Effect.js';

function updateSavedEncounters(jsonArray, newEncounter) {
    if(jsonArray === null) jsonArray = []
    
    // Find the index of the existing object with the same encounterGuid
    const index = jsonArray.findIndex(item => item['encounterGuid'] === newEncounter['encounterGuid']);

    let saveType = "Saved Changes to Existing - "
    if (index !== -1) {
        jsonArray[index] = newEncounter;
    } else {
        saveType = "Saved New Encounter - "
        jsonArray.push(newEncounter);
    }

    console.log("%c" + saveType + newEncounter.encounterName + " (" + newEncounter.encounterGuid + " - " + (newEncounter.folder_dir || "[No folder]") + ")", "background: #fdfd96;")

    return jsonArray;
}

function getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

const iJson = (i) => {
    return {id: getRandomInt(1, 9999), index: i, removing: false, newlyAdded: true}
}

const effectConditionImgMap = {...effectImgMap, ...conditionImgMap}

const EncounterColumn = ({currentEncounter, handleLoadEncounter, refreshLoading, setCurrentEncounter, setDefaultBackgroundOverride, setPlayerViewBackground, savedEncounters, setSavedEncounters, handleRefresh, refreshCheck, autoRefresh, showSearchList, handleNewEncounter, setEncounterGuid, handleUploadMonsterImage, resort, selectedIndex, setSelectedIndex, socket}) => {
    const {username} = useUser();
    const [roundNum, setRoundNum] = useState(currentEncounter.roundNum);
    const [turnNum, setTurnNum] = useState(currentEncounter.turnNum);
    const [nameChange, setNameChange] = useState(false)
    const [quickEdit, setQuickEdit] = useState(false)
    const [removalNotice, setRemovalNotice] = useState({creature: null, removedEffects: []});

    const [alreadySelected, setAlreadySelected] = useState(false);
    const [newlyAdded, setNewlyAdded] = useState(false);
    const animationDuration = 500; // Duration in milliseconds
    let maxNum = showSearchList ? 2 : 3

    useEffect(() => {
        if(nameChange) {
            handleSaveEncounter()
            setNameChange(false)
        }
        // eslint-disable-next-line
    }, [nameChange]);
    
    useEffect(() => {
        if(showSearchList && selectedIndex.length > 2) {
            let arr = [...selectedIndex]
            arr.splice(2, 1)
            setSelectedIndex([...arr])
        }
            
    }, [showSearchList]);

    useEffect(() => {
        let noCreatures = currentEncounter.creatures?.length === 0
        if(noCreatures) {
            setSelectedIndex([])
        }

    }, [currentEncounter.creatures]);

    // When changing encounters watch the turnNum and roundNum to rerender the turn controller
    useEffect(() => {

        const handleArrowKeys = (e) => {
            // Only trigger if not focusing an input, textarea, or contenteditable element
            const tag = document.activeElement.tagName.toLowerCase();
            const isEditable = document.activeElement.isContentEditable;
            if (tag === "input" || tag === "textarea" || isEditable) return;
            if (e.key === "ArrowLeft") {
                handleTurnNums("prev");
            } else if (e.key === "ArrowRight") {
                handleTurnNums("next");
            }
        };
        
        handleSaveEncounter()

        window.addEventListener("keydown", handleArrowKeys);
        return () => window.removeEventListener("keydown", handleArrowKeys);

    }, [roundNum, turnNum, currentEncounter]);

    useEffect(() => {
        if (!removalNotice) return;

        const timer = window.setTimeout(() => {
            setRemovalNotice({creature: null, removedEffects: []});
        }, 10000);

        return () => window.clearTimeout(timer);
    }, [removalNotice]);

    // Subtract from a effects duration at the end of their turn
    const checkEffectsOnTurnChange = (creature) => {
        if(creature?.effectObjs?.length > 0) {

            let removedEffects = [];
            let updatedEffects = creature.effectObjs.map(effect => {
                // If the effect is infinite, don't change it
                if(effect.duration === '∞') {
                    return effect
                }
                let updatedEffect = {...effect}
                updatedEffect.duration = (parseInt(updatedEffect.duration) - 1).toString()

                const action = updatedEffect.duration === '0' ? 'remove' : 'minus';
                // keep it a string for the db
                if(updatedEffect.duration === '0')
                    removedEffects.push(effect);
               
                socket.emit('creatureEffectChange', updatedEffect, action, creature.creatureGuid, "dm");
                return updatedEffect

            }).filter(effect => parseInt(effect.duration) > 0 || effect.duration === '∞');

            if(removedEffects.length > 0) {
                setRemovalNotice({creature: creature, removedEffects});
            }

            setCurrentEncounter(prev => {
                return {...prev, creatures: prev.creatures.map(c => c.creatureGuid === creature.creatureGuid ? {...c, effectObjs: updatedEffects} : c)}
            });
        }
    }

    // Reset legendary actions recharge count when a creature start their turn 
    const checkLegendaryActionsChange = (creature) => {
        let rechargeCount = creature?.legendary_actions_count
        if(rechargeCount > 0) {

            let rechargeLeftOver = rechargeCount % 10
            let newCount = rechargeCount - rechargeLeftOver
            // socket.emit('creatureEffectChange', updatedEffect, action, creature.creatureGuid, "dm");
            socket?.emit('statBlockEdit', creature.creatureGuid, "legendary_actions_count", newCount);

            setCurrentEncounter(prev => {
                return {...prev, creatures: 
                    prev.creatures.map(c => c.creatureGuid === creature.creatureGuid 
                        ? {...c, legendary_actions_count: newCount} 
                        : c
                    )
                }
            });
        }
    }

     const handleTurnNums = (action = null, e = null) => {
        let encounterLength = currentEncounter.creatures.length
        let newTurn = null;
        let newRound = null;

        if(action === "next") {
            if(roundNum === 0) {
                newRound = 1
            }

            if(turnNum === encounterLength) {
                newRound = roundNum + 1
                newTurn = 1
            } else {
                newTurn = turnNum + 1
            }

            checkEffectsOnTurnChange(currentEncounter.creatures[newTurn-1])
            checkLegendaryActionsChange(currentEncounter.creatures[newTurn-1])
        } else if(action === "prev") {
            let minTurn = 1
            if (roundNum >= 1 || turnNum > minTurn) {
                if(roundNum === 1 && turnNum === 1) {
                    newTurn = 0
                    newRound = 0
                    
                } else if(turnNum === 1) {
                    newRound = roundNum-1
                    newTurn = encounterLength
                    
                } else {
                    newTurn = turnNum - 1
                }
            }
        } else if(action === "reset") {
            newRound = 0
            newTurn = 0
        }

        // Either way set everything
        if(action !== null) {
            if(newRound !== null && newRound !== roundNum) {
                currentEncounter.roundNum = newRound
                setRoundNum(newRound)
                socket.emit("roundNumChange", newRound, currentEncounter.encounterGuid)
            }

            if(newTurn !== null && newTurn !== turnNum) {
                currentEncounter.turnNum = newTurn
                setTurnNum(newTurn)
                socket.emit("turnNumChange", newTurn, currentEncounter.encounterGuid)       
            }

        }

        return {"roundNum": roundNum, "turnNum": turnNum}
    }

    useEffect(() => {
        setSelectedIndex([])
        setRoundNum(currentEncounter.roundNum)
        setTurnNum(currentEncounter.turnNum)
    }, [currentEncounter.encounterGuid]);

    const clickEncounterCreatureX = (event, xCreature, xIndex) => {
        event.stopPropagation(); 

        if(selectedIndex.length > 0) {
            let foundIndex = selectedIndex.findIndex(obj => obj.index === xIndex);
            if(foundIndex > -1) {
                // if deleting the creature currently selected unselect it
                let newArr = selectedIndex
                newArr.splice(foundIndex, 1)
            }

            // if deleting a creature of lower index then selectedIndex move selectedIndex down by 1 to follow the selected creatures object
            let newArr = [...selectedIndex]
            newArr.forEach((sIndex, i) => {
                if(xIndex < sIndex.index) {
                    newArr[i].index = sIndex.index - 1
                } 
            });
            setSelectedIndex([...newArr])
        }

        // Move the turn down when creatures are removed
        if((turnNum > xIndex+1 || currentEncounter.creatures.length === turnNum) && currentEncounter.creatures.length !== 1) {
            handleTurnNums('prev')
        } 

        let newArray = currentEncounter.creatures.filter((_, i) => i !== xIndex)
        setCurrentEncounter(prev => ({...prev, creatures: [...newArray]}));

        socket.emit("removeCreatureFromEncounter", currentEncounter.encounterName, xCreature.creatureGuid, username)
    };  

    // This is mostly for handling ui, not saving to the database
    // Since database changes happen on a lower level to avoid bloat
    const handleSaveEncounter = () => {
        let newEncounter = {
            encounterName: currentEncounter.encounterName,
            encounterGuid: currentEncounter.encounterGuid || generateUniqueId(),
            roundNum: roundNum,
            turnNum: turnNum,
            creatures: currentEncounter.creatures,
            backgroundGuid: currentEncounter.backgroundGuid,
            playerViewOptions: currentEncounter.playerViewOptions,
            hideEnemies: currentEncounter.hideEnemies,
            username: currentEncounter?.username,
            folder_dir: currentEncounter.folder_dir,
            spotifyEncounterSongInfo: currentEncounter.spotifyEncounterSongInfo
        }

        if(newEncounter.encounterGuid !== currentEncounter.encounterGuid) {
            setEncounterGuid(newEncounter.encounterGuid) 
        }

        // Overwrites if exists, appends if new
        const updatedSavedEncountersList = updateSavedEncounters(savedEncounters, newEncounter);
        // Setting this list to update the encounter list
        setSavedEncounters(updatedSavedEncountersList)
    };

    const handleAddExtra = (type) => {
        let newDummy = {}
        if(type === "global") {
            newDummy = {
                ...envObject
            }
        } else {
            let url = "https://www.dndbeyond.com/Content/Skins/Waterdeep/images/icons/monsters"
            let beastImg = url + "/beast.jpg"
            let humanImg = url + "/humanoid.jpg"
            newDummy = {
                ...dummyDefault,
                avatarUrl: type === "monster" ? beastImg : humanImg,
                name: type === "monster" ? "Dummy Monster" : "Dummy Player",
                alignment: type === "monster" ? "enemy" : "ally",
                border: type === "monster" ? COLOR_RED : COLOR_GREEN,
                type: type
            }
        }

        newDummy.creatureGuid = generateUniqueId()
        newDummy.encounterGuid = currentEncounter.encounterGuid;
        newDummy.hidden = true;
        
        socket.emit("addCreatureToEncounter", newDummy)

        if(currentEncounter.creatures.length === 0 && currentEncounter.encounterName === INIT_ENCOUNTER_NAME) {
            socket.emit("newEncounter", currentEncounter.encounterGuid, '', currentEncounter.folder_dir)
        }

        setCurrentEncounter(prev => ({...prev, creatures: [...prev.creatures, newDummy]}));
    };

    const handleAutoRollInitiative = (type, event) => {
        event.stopPropagation()
        let initatives = []
        
        if(type === "clear") {
            currentEncounter.creatures.forEach(creature => {
                creature.initiative = 0
                initatives.push({creatureGuid: creature.creatureGuid, initiative: 0})
            });   
        } else {
            let groupSet = {}
            currentEncounter.creatures.forEach(creature => {
                if(creature.type !== "player") {
                    // No group guid or excluded group member
                    let initBonus = Math.floor(parseInt((creature.dexterity-10)/2) ?? 0)
                    let newInit = Math.floor(Math.random() * 20) + 1 + initBonus

                    let gGuid = creature.groupGuid
                    // if part of a group and not excluded
                    if(gGuid && gGuid[0] !== '!') {
                        // If group already rolled use that set newInit to that roll
                        if(groupSet[gGuid]) {
                            newInit = groupSet[gGuid]
                        } else { // If group has NOT rolled yet set in group -> newInit is used later
                            groupSet[gGuid] = newInit
                        }
                    }
                    
                    creature.initiative = newInit
                    initatives.push({creatureGuid: creature.creatureGuid, initiative: newInit})
                }
            });
        }

        socket.emit("autoRolledInitiative", initatives, currentEncounter.encounterGuid);
        resort()
    }   
    
    //handles adding the new and removing the old
    const handleRemoveFromSelectedIndex = (xIndex, check = false) => {
        let updatedColumns = [...selectedIndex]
        if(!check) {
            const updatedColumns = [...selectedIndex];
            selectedIndex[xIndex].removing = true
            setSelectedIndex(updatedColumns)

            setTimeout(() => {
                setSelectedIndex((prev) => prev.filter((_, i) => i !== xIndex));
            }, animationDuration);

        } else {
            const indexOf = selectedIndex.findIndex(obj => obj.index === xIndex);
            // remove if clicked creature is duplicate
            if(indexOf > -1) {
                updatedColumns.splice(indexOf, 1);
            // If no duplicates, check length for room
            } else if(updatedColumns.length === maxNum) {
                updatedColumns.splice(updatedColumns.length-1)
            }

            updatedColumns = [iJson(xIndex), ...updatedColumns]
            if(indexOf !== 0){
                setSelectedIndex(updatedColumns)
                setNewlyAdded(true);
            } else {
                setAlreadySelected(true)
            }
            
        }
    };

    useEffect(() => {
        if (newlyAdded) {
            setTimeout(() => {
                setSelectedIndex((prev) =>
                    prev.map((block) => ({ ...block, newlyAdded: false }))
                );
                setNewlyAdded(false);
            }, animationDuration); // Small delay to ensure DOM renders first
        }
    }, [newlyAdded]);

    useEffect(() => {
        if (alreadySelected) {
            setTimeout(() => {
                setAlreadySelected(false);
            }, 1000);
        }
    }, [alreadySelected]);
    
    let flexNum = selectedIndex?.length
    if(flexNum > 2)
        flexNum = 2

    return (
        <>
            <div className='column'>
                <div className='infoContainer'>
                    <EncounterColumnTop savedEncounters={savedEncounters} handleLoadEncounter={handleLoadEncounter} setSavedEncounters={setSavedEncounters} handleNewEncounter={handleNewEncounter} setNameChange={setNameChange} refreshLoading={refreshLoading} setPlayerViewBackground={setPlayerViewBackground} handleTurnNums={handleTurnNums} handleRefresh={handleRefresh} refreshCheck={refreshCheck} autoRefresh={autoRefresh} currentEncounter={currentEncounter} setCurrentEncounter={setCurrentEncounter} handleAutoRollInitiative={handleAutoRollInitiative} quickEdit={quickEdit} setQuickEdit={setQuickEdit} setDefaultBackgroundOverride={setDefaultBackgroundOverride} socket={socket} />
                    {currentEncounter.creatures.length ? (
                        <EncounterList currentEncounter={currentEncounter} setCurrentEncounter={setCurrentEncounter} handleSaveEncounter={handleSaveEncounter} turnNum={turnNum} handleUploadMonsterImage={handleUploadMonsterImage} selectedIndex={selectedIndex} handleRemoveFromSelectedIndex={handleRemoveFromSelectedIndex} clickEncounterCreatureX={clickEncounterCreatureX} resort={resort} quickEdit={quickEdit} socket={socket}/>
                    ) : (
                        <div className='encounterCreaturesNoItemsContainer'> 
                            <div className='encounterCreaturesNoItems'>
                                {savedEncounters?.length ? (
                                    <>
                                        Add creatures or select one of your Encounters!
                                        <DropdownMenu savedEncounters={savedEncounters} setSavedEncounters={setSavedEncounters} handleLoadEncounter={handleLoadEncounter} currentEncounter={currentEncounter} setCurrentEncounter={setCurrentEncounter} socket={socket}/> 
                                    </>
                                ) : ( 
                                    <>Add creatures to create an encounter!</>
                                )}
                            </div>
                        </div>
                    )}
                    <div className='dummyButtons'>
                        <hr className='seperator'/> 
                        <div>
                            <button className='dmViewButton' onClick={() => handleAddExtra('player')}> Add Player </button>
                            <button className='dmViewButton' onClick={() => handleAddExtra('monster')}> Add Creature </button>
                            <button className='dmViewButton' onClick={() => handleAddExtra('global')}> Add Lair Tracker </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* this flexNum allows the other columns to take space when no selected creature is present */}
            <div className='statContainers' style={{flex: flexNum}}>
                {selectedIndex.map((sIndex, xIndex) => {
                    return <div key={sIndex.id}
                                className={`column 
                                    ${sIndex.newlyAdded ? "newly-added" : ""}
                                    ${!sIndex.removing && !sIndex.newlyAdded && newlyAdded ? "existing-slide" : ""}
                                    ${sIndex.removing ? "removing" : ""}
                                    ${alreadySelected && xIndex === 0 ? "alreadySelected" : ""}
                                `}
                            >
                                {currentEncounter.creatures[sIndex.index] &&
                                    <div className='statBlock'>
                                        <StatBlock selectedIndex={sIndex.index} indexOf={xIndex} currentEncounter={currentEncounter} setCurrentEncounter={setCurrentEncounter} closeStatBlock={() => handleRemoveFromSelectedIndex(xIndex)} handleUploadMonsterImage={handleUploadMonsterImage} socket={socket}/>
                                    </div>
                                }
                            </div>
                })}
            </div>
            {removalNotice.creature && (
                <div className="effectRemovalNotice" role="status" aria-live="polite">
                    <div className="effectRemovalTitle">Effects Removed</div> 
                    <div className="effectRemovalCreature">
                        <div className="effectRemovalIconContainer">
                            <img className="effectRemovalIcon" src={removalNotice.creature?.avatarUrl} alt={"list Icon"} />
                        </div>
                        {removalNotice.creature?.name}
                    </div> 
                    <hr/>
                    <div className="effectRemovalGrid">
                        {removalNotice.removedEffects.map((effect, index) => (
                            <Effect 
                                key={effect.effectName}
                                effectName={effect.effectName}
                                image={effectConditionImgMap[effect.effectName]}
                            />
                        ))}
                    </div>
                </div>
            )}
        </>
  );
}

export default EncounterColumn;